import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BOOT_BACKGROUND } from './BootSplash';
import logo from '../assets/branding/logo-rounded-1024.png';

// F6.α it.2 — dwell mínimo del estado "Reintentando…" del botón. Sin él, un
// intento que falla en <100ms (sin red, el error es casi instantáneo) no
// produce NINGÚN cambio visible y el botón parece muerto (hallazgo del smoke
// on-device). CONCURRENTE con el intento, jamás aditivo — mismo patrón
// anti-aditivo del splash de F4.3b:
//   duración visible = max(intento real, RETRY_FEEDBACK_MIN_MS)
// (si el intento tarda 2s, el botón se re-habilita al terminar — no se suman
// 700ms encima; el mínimo solo rellena cuando el intento fue más rápido).
export const RETRY_FEEDBACK_MIN_MS = 700;

/**
 * OfflineBootScreen — estado de arranque "sin conexión" del build nativo (F6.α).
 *
 * Se pinta cuando hay tokens en el Keychain pero el refresh de arranque falló
 * por causa TRANSITORIA (red/timeout/5xx — nunca por 401/403; ver
 * isAuthRejection en lib/api.js). Es la alternativa honesta a lo que pasaba
 * antes: borrar la sesión válida y mandar a signup.
 *
 * Estilo BootSplash a propósito: mismo fondo #111111, mismo logo, cero spinner
 * (regla F4.3b: o pantalla correcta, o logo — aquí, pantalla correcta). A
 * diferencia del splash, SÍ recibe taps (botón Reintentar); el reintento
 * automático corre igual por debajo (AuthContext, BOOT_RETRY_MS + focus).
 *
 * Feedback del botón (it.2): tocar Reintentar deshabilita el botón y muestra
 * "Reintentando…" durante max(intento, RETRY_FEEDBACK_MIN_MS); si el intento
 * falla, el ciclo deshabilitado→habilitado ES el feedback (mensaje intacto).
 * El estado es LOCAL y solo lo enciende el gesto del usuario — el reintento
 * automático de AuthContext nunca pasa por aquí (decisión: el botón no refleja
 * intentos ajenos). Un tap durante un intento automático en vuelo no apila
 * nada: onRetry (retryBootRefresh) ya se serializa con bootRetryInFlight.
 *
 * Desde F6.4b esta pantalla es el CAMINO RESIDUAL del boot sin red: con
 * bodega poblada del mismo dueño la app ENTRA en modo offline y esto no se
 * pinta (AuthBootGate: bootOffline && !user). Se llega aquí solo sin bodega
 * utilizable — instalación fresca, dueño distinto, o logout previo.
 */
export default function OfflineBootScreen({ onRetry }) {
  const { t } = useTranslation('auth');
  const [retrying, setRetrying] = useState(false);
  // Si el reintento tiene éxito, AuthContext apaga bootOffline y este componente
  // se desmonta con el timer del dwell aún vivo — el ref evita el setState
  // sobre un componente desmontado.
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const handleRetry = async () => {
    if (retrying) return; // el disabled ya lo impide; cinturón por si acaso
    setRetrying(true);
    // Dwell concurrente: el intento y el mínimo corren EN PARALELO y se espera
    // al más lento (max, no suma) — ver RETRY_FEEDBACK_MIN_MS arriba.
    const minHold = new Promise((resolve) => setTimeout(resolve, RETRY_FEEDBACK_MIN_MS));
    try {
      await Promise.all([onRetry(), minHold]);
    } finally {
      if (mountedRef.current) setRetrying(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center gap-6 px-8 text-center"
      style={{ backgroundColor: BOOT_BACKGROUND }}
      data-testid="offline-boot"
    >
      <img src={logo} alt="" className="w-24 h-24" data-testid="offline-boot-logo" />
      <p className="text-sm leading-relaxed text-neutral-300 max-w-xs">
        {t('boot.offline_message')}
      </p>
      <button
        type="button"
        onClick={handleRetry}
        disabled={retrying}
        className="px-6 py-2.5 rounded-full border border-neutral-600 text-sm font-medium text-neutral-100 active:opacity-70 disabled:opacity-50"
        data-testid="offline-boot-retry"
      >
        {t(retrying ? 'boot.offline_retrying' : 'boot.offline_retry')}
      </button>
    </div>
  );
}
